import { storeToRefs } from 'pinia'
import { useEditorStore } from '@/stores/editor.ts'
import { useCommonStore } from '@/stores/common.ts'
import { directSocketSave, deletePrefix } from '@/views/editor/composable/useNote.ts'
import { UseHistory } from '@/views/editor/composable/useHistory.ts'


const MIN_IMAGE_SIZE = 40
export function useImageSize() {
	const lineLeftColor: string = 'linear-gradient(to right, #01C6FF, #B069FF, #FF619A, #FFA401, #01C6FF, #B069FF, #FF619A, #FFA401, #01C6FF)'
	const lineTopColor: string = 'linear-gradient(to top, #01C6FF, #B069FF, #FF619A, #FFA401, #01C6FF, #B069FF, #FF619A, #FFA401, #01C6FF)'
	let resizeTarget: any = null
	let prevWidthPosition: number = 0
	let prevHeightPosition: number = 0
	let originWidth: number = 0
	let originHeight: number = 0
	let currentWidth: number = 0
	let currentHeight: number = 0
	let originalImageWidth: number = 0
	let originalImageHeight: number = 0
	let originalImageLeft: number = 0
	let originalImageTop: number = 0
	let startMouseX: number = 0
	let startMouseY: number = 0
	let startWidth: number = 0
	let startHeight: number = 0

	function setWidth() {
		const { enterSelector } = storeToRefs(useEditorStore())
		if (!enterSelector.value) return

		const imageContainer = enterSelector.value.querySelector('.image--container')

		const createWidthElement = document.createElement('div')
		createWidthElement.classList.add('image--container__width')
		createWidthElement.style.background = lineTopColor
		createWidthElement.style.backgroundSize = '200% 200%'
		createWidthElement.style.cursor = 'col-resize'

		imageContainer?.appendChild(createWidthElement)

		createWidthElement.addEventListener('mousedown', imageDragStartEvent)
	}

	function setHeight() {
		const { enterSelector } = storeToRefs(useEditorStore())
		if (!enterSelector.value) return

		const imageContainer = enterSelector.value.querySelector('.image--container')

		const createHeightElement = document.createElement('div')
		createHeightElement.classList.add('image--container__height')
		createHeightElement.style.background = lineLeftColor
		createHeightElement.style.backgroundSize = '200% 200%'
		createHeightElement.style.cursor = 'row-resize'

		imageContainer?.appendChild(createHeightElement)

		createHeightElement.addEventListener('mousedown', imageDragStartEvent)
	}

	function setBoth() {
		const { enterSelector } = storeToRefs(useEditorStore())
		if (!enterSelector.value) return

		const imageContainer = enterSelector.value.querySelector('.image--container')

		const createWidthElement = document.createElement('div')
		createWidthElement.classList.add('image--container__both-width')
		createWidthElement.style.cursor = 'nwse-resize'

		const createHeightElement = document.createElement('div')
		createHeightElement.classList.add('image--container__both-height')
		createHeightElement.style.cursor = 'nwse-resize'

		imageContainer?.appendChild(createWidthElement)
		imageContainer?.appendChild(createHeightElement)

		createWidthElement.addEventListener('mousedown', imageDragStartEvent)
		createHeightElement.addEventListener('mousedown', imageDragStartEvent)
	}

	function imageDragStartEvent(event: any) {
		event.preventDefault()
		event.stopPropagation()
		
		const { isImageSize, noteUid, noteType } = storeToRefs(useEditorStore())
		isImageSize.value = true
		const { target } = event
		resizeTarget = target
		const classAry = resizeTarget.classList

		const targetContainerInit = resizeTarget.closest('.editor--block')

		if (noteType.value === 'Project' && targetContainerInit?.id) {
			const { socket } = storeToRefs(useCommonStore())
			socket.value.blockEdit(noteUid.value, deletePrefix(targetContainerInit.id))
		}
		const resizeContainerInit = targetContainerInit.querySelector('.image--container')
		const resizeImageInit = resizeContainerInit.querySelector('img')
		originWidth = Math.round(resizeContainerInit.getBoundingClientRect().width)
		originHeight = Math.round(resizeContainerInit.getBoundingClientRect().height)
		currentWidth = originWidth
		currentHeight = originHeight
		startWidth = originWidth
		startHeight = originHeight
		originalImageWidth = Math.round(resizeImageInit.getBoundingClientRect().width)
		originalImageHeight = Math.round(resizeImageInit.getBoundingClientRect().height)
		originalImageLeft = parseFloat(resizeImageInit.style.left || '0')
		originalImageTop = parseFloat(resizeImageInit.style.top || '0')

		if (classAry.contains('image--container__width')) {
			document.body.style.cursor = 'ew-resize'
		} else if (classAry.contains('image--container__height')) {
			document.body.style.cursor = 'ns-resize'
		} else {
			document.body.style.cursor = 'nwse-resize'
		}
		if (prevWidthPosition === 0) {
			prevWidthPosition = event.clientX
			startMouseX = event.clientX
		}

		if (prevHeightPosition === 0) {
			prevHeightPosition = event.clientY
			startMouseY = event.clientY
		}

		document.addEventListener('mousemove', imageDragMoveEvent)
		document.addEventListener('mouseup', imageDragEndEvent)
	}

	const imageDragMoveEvent = (event: any) => {
		const targetContainer = resizeTarget.closest('.editor--block')
		const resizeContainer = targetContainer.querySelector('.image--container')
		const resizeImage = resizeContainer.querySelector('img')

		if (prevWidthPosition === 0) {
			prevWidthPosition = event.clientX
			startMouseX = event.clientX
			if (currentWidth === 0) {
				currentWidth = Math.round(resizeContainer.getBoundingClientRect().width)
				startWidth = currentWidth
			}
		}

		prevWidthPosition = event.clientX

		let width = startWidth

		if (resizeTarget.classList.value.includes('width') || resizeTarget.classList.value.includes('both')) {
			width = startWidth + (event.clientX - startMouseX)
		}
		
		const scaleRatioWidth = width / (originWidth || 1)
		const resizeImageWidth = Math.round(originalImageWidth * scaleRatioWidth)
		const resizeLeftValue = Math.round(originalImageLeft * scaleRatioWidth)

		if (width < MIN_IMAGE_SIZE) {
			width = MIN_IMAGE_SIZE
		}

		if (prevHeightPosition === 0) {
			prevHeightPosition = event.clientY
			startMouseY = event.clientY
			if (currentHeight === 0) {
				currentHeight = Math.round(resizeContainer.getBoundingClientRect().height)
				startHeight = currentHeight
			}
		}

		prevHeightPosition = event.clientY

		let height = startHeight

		if (resizeTarget.classList.value.includes('height') || resizeTarget.classList.value.includes('both')) {
			height = startHeight + (event.clientY - startMouseY)
		}
		
		const scaleRatioHeight = height / (originHeight || 1)
		const resizeImageHeight = Math.round(originalImageHeight * scaleRatioHeight)
		const resizeTopValue = Math.round(originalImageTop * scaleRatioHeight)

		if (height < MIN_IMAGE_SIZE) {
			height = MIN_IMAGE_SIZE
		}

		const finalWidth = Math.round(width)
		const finalHeight = Math.round(height)

		if (resizeContainer.dataset.crop && resizeContainer.dataset.crop === 'true') {
			if (width >= MIN_IMAGE_SIZE) {
				resizeImage.style.width = `${resizeImageWidth}px`
				resizeImage.style.maxWidth = `${resizeImageWidth}px`
				resizeImage.style.left = `${resizeLeftValue}px`
			} else {
				resizeImage.style.width = `${MIN_IMAGE_SIZE}px`
				resizeImage.style.maxWidth = `${MIN_IMAGE_SIZE}px`
			}

			if (height >= MIN_IMAGE_SIZE) {
				resizeImage.style.height = `${resizeImageHeight}px`
				resizeImage.style.maxHeight = `${resizeImageHeight}px`
				resizeImage.style.top = `${resizeTopValue}px`
			} else {
				resizeImage.style.height = `${MIN_IMAGE_SIZE}px`
				resizeImage.style.maxHeight = `${MIN_IMAGE_SIZE}px`
			}
		} else {
			resizeImage.style.width = `${finalWidth}px`
			resizeImage.style.maxWidth = `${finalWidth}px`
			resizeImage.style.height = `${finalHeight}px`
			resizeImage.style.maxHeight = `${finalHeight}px`
		}

		if (event.shiftKey) {
			width = currentWidth
			height = originHeight + (width - originWidth)
		}

		// ✅ 최종 크기도 정수로 적용
		resizeContainer.style.width = `${finalWidth}px`
		resizeContainer.style.height = `${finalHeight}px`

		resizeContainer.dataset.width = `${finalWidth}px`
		resizeContainer.dataset.height = `${finalHeight}px`

		currentWidth = finalWidth
		currentHeight = finalHeight
	}

	const imageDragEndEvent = async (event: any) => {
		const { isImageSize, enterSelector, noteUid, noteType } = storeToRefs(useEditorStore())
		document.body.style.removeProperty('cursor')

		const targetBlock = resizeTarget.closest('.editor--block')

		if (noteType.value === 'Project' && targetBlock?.id) {
			const { socket } = storeToRefs(useCommonStore())
			socket.value.blockBlur(noteUid.value, deletePrefix(targetBlock.id))
		}

		originWidth = 0
		originHeight = 0

		prevWidthPosition = 0
		prevHeightPosition = 0

		currentWidth = 0
		currentHeight = 0
		originalImageWidth = 0
		originalImageHeight = 0
		originalImageLeft = 0
		originalImageTop = 0
		
		startMouseX = 0
		startMouseY = 0
		startWidth = 0
		startHeight = 0

		document.removeEventListener('mousemove', imageDragMoveEvent)
		document.removeEventListener('mouseup', imageDragEndEvent)

		isImageSize.value = false

		const imageContainer = targetBlock?.querySelector('.image--container')
		if (imageContainer) {
			imageContainer.querySelector('.image--container__width')?.remove()
			imageContainer.querySelector('.image--container__height')?.remove()
			imageContainer.querySelector('.image--container__both-width')?.remove()
			imageContainer.querySelector('.image--container__both-height')?.remove()
		}

		await directSocketSave('image', targetBlock)
		new UseHistory().push()

		if (imageContainer) {
			enterSelector.value = targetBlock
			setBoth()
			setWidth()
			setHeight()
		}
	}

	return {
		setWidth,
		setHeight,
		setBoth,
	}
}
